// elements/theming/color-shades.ts
import { namedColors } from "./css-named-colors.js";
var toRawColor = (color) => {
  if (color in namedColors)
    color = namedColors[color];
  const hexMatch = /^#(\w{2})(\w{2})(\w{2})(\w{2})?$|^#(\w)(\w)(\w)(\w)?$/.exec(color);
  if (hexMatch) {
    if (hexMatch[1] && hexMatch[2] && hexMatch[3]) {
      const r2 = parseInt(hexMatch[1], 16) / 255;
      const g2 = parseInt(hexMatch[2], 16) / 255;
      const b2 = parseInt(hexMatch[3], 16) / 255;
      const a2 = hexMatch[4] ? parseInt(hexMatch[4], 16) / 255 : 1;
      return { r: r2, b: b2, g: g2, a: a2 };
    }
    const r = parseInt(`${hexMatch[5]}${hexMatch[5]}`, 16) / 255;
    const g = parseInt(`${hexMatch[6]}${hexMatch[6]}`, 16) / 255;
    const b = parseInt(`${hexMatch[7]}${hexMatch[7]}`, 16) / 255;
    const a = hexMatch[8] ? parseInt(`${hexMatch[8]}${hexMatch[8]}`, 16) / 255 : 1;
    return { r, b, g, a };
  }
  const rgbaMatch = /^rgb\(([0-9]{1,3}),([0-9]{1,3}),([0-9]{1,3})\)$|^rgba\(([0-9]{1,3}),([0-9]{1,3}),([0-9]{1,3}),([0-9]{1,3})\)$/.exec(
    color
  );
  if (rgbaMatch) {
    const r = parseInt(rgbaMatch[1] ?? rgbaMatch[5]) / 255;
    const g = parseInt(rgbaMatch[2] ?? rgbaMatch[6]) / 255;
    const b = parseInt(rgbaMatch[3] ?? rgbaMatch[7]) / 255;
    const a = rgbaMatch[4] ? parseInt(rgbaMatch[4] ?? rgbaMatch[8]) / 255 : 1;
    return { r, b, g, a };
  }
  throw new Error(
    "Given string is neither a hex color nor a rgb color! Supported strings examples: #123(4), #123456(78), rgb(1,23,45), rgba(234,23,12,45)"
  );
};
var rawToHex = (color) => {
  return `#${Math.round(color.r * 255).toString(16).padStart(2, "0")}${Math.round(color.g * 255).toString(16).padStart(2, "0")}${Math.round(color.b * 255).toString(16).padStart(2, "0")}${Math.round(color.a * 255).toString(16).padStart(2, "0")}`;
};
var lighten = (color, amount) => {
  return rawToHex(rgbLogShade(toRawColor(color), amount * 0.1));
};
var darken = (color, amount) => {
  return rawToHex(rgbLogShade(toRawColor(color), -amount * 0.1));
};
var rgbLogShade = (color, ratio) => {
  const pTemp = ratio < 0;
  const t = pTemp ? 0 : ratio * 255 ** 2;
  const P = pTemp ? 1 + ratio : 1 - ratio;
  return {
    r: Math.round((P * (color.r * 255) ** 2 + t) ** 0.5) / 255,
    g: Math.round((P * (color.g * 255) ** 2 + t) ** 0.5) / 255,
    b: Math.round((P * (color.b * 255) ** 2 + t) ** 0.5) / 255,
    a: color.a
  };
};
var colorNetwork = {
  layers: [
    { r: {}, g: {}, b: {} },
    {
      "0": {
        bias: 14.976861159083922,
        weights: { r: -0.8490078565616036, g: -31.755459824961406, b: 8.959898683864875 }
      },
      "1": {
        bias: 11.41726668638475,
        weights: { r: 2.041674433995231, g: -14.642667192099287, b: -19.42305451259349 }
      },
      "2": {
        bias: -5.390992999224415,
        weights: { r: 10.669737768649634, g: 3.1833977040718335, b: -1.9877571885751582 }
      }
    },
    {
      black: {
        bias: 5.751933259834488,
        weights: { "0": -22.4348054863413, "1": -15.532347865839611, "2": 17.717933755912604 }
      }
    }
  ],
  outputLookup: true,
  inputLookup: true
};
var runNetwork = (color) => {
  let input = color;
  let output = {};
  for (let i = 1; i < colorNetwork.layers.length; i++) {
    const layer = colorNetwork.layers[i];
    for (const id in layer) {
      const node = layer[id];
      let sum = node.bias;
      for (const iid in node.weights) {
        sum += node.weights[iid] * input[iid];
      }
      output[id] = 1 / (1 + Math.exp(-sum));
    }
    input = output;
  }
  return output;
};
function textColorFromBackgroundColor(bgColor, lightColor, darkColor) {
  const uicolors = { r: bgColor.r, g: bgColor.g, b: bgColor.b };
  const prediction = runNetwork(uicolors).black;
  if (prediction > 0.5)
    return darkColor;
  return lightColor;
}
export {
  darken,
  lighten,
  rawToHex,
  rgbLogShade,
  textColorFromBackgroundColor,
  toRawColor
};
